import React from 'react';

import type { FmConfirmationCopyDescriptor } from './FmConfirm';
import { FmFormFlowSurface } from './FmFormFlowPage';
import type { FmFormFlowPageDescriptor } from './FmFormFlowPage.types';
import { FmModal } from './FmModal';

export interface FmFormFlowDialogProps {
  open: boolean;
  title: string;
  closeLabel: string;
  size: 'md' | 'lg';
  descriptor: FmFormFlowPageDescriptor;
  closeConfirmation?: FmConfirmationCopyDescriptor;
  onClose: () => void;
  'data-testid'?: string;
}

/** Governed form workflow displayed over a persistent spatial workspace. */
export const FmFormFlowDialog: React.FC<FmFormFlowDialogProps> = ({
  open,
  title,
  closeLabel,
  size,
  descriptor,
  closeConfirmation,
  onClose,
  'data-testid': dataTestId,
}) => {
  const [closePending, setClosePending] = React.useState(false);

  React.useEffect(() => {
    if (!open) setClosePending(false);
  }, [open]);

  const requestClose = React.useCallback(() => {
    if (closeConfirmation) {
      setClosePending(true);
      return;
    }
    onClose();
  }, [closeConfirmation, onClose]);

  return (
    <>
      <FmModal
        open={open && !closePending}
        title={title}
        size={size}
        onCancel={requestClose}
        cancelText={closeLabel}
        footer={null}
        data-testid={dataTestId}
      >
        <FmFormFlowSurface
          descriptor={descriptor}
          presentation="dialog"
          data-testid={dataTestId ? `${dataTestId}-flow` : undefined}
        />
      </FmModal>
      {closeConfirmation ? (
        <FmModal
          open={open && closePending}
          title={closeConfirmation.title}
          size="sm"
          onOk={onClose}
          okText={closeConfirmation.confirmLabel}
          onCancel={() => setClosePending(false)}
          cancelText={closeConfirmation.cancelLabel}
          dismissible={false}
          data-testid={dataTestId ? `${dataTestId}-close-confirmation` : undefined}
        >
          {closeConfirmation.description}
        </FmModal>
      ) : null}
    </>
  );
};

FmFormFlowDialog.displayName = 'FmFormFlowDialog';

export default FmFormFlowDialog;
